import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../i18/config";
import { applyWorkflowTemplate, getWorkflowTemplates, removeWorkflowTemplate } from "../../api";
import * as route from "../../api/routes";
import BreadCrumb from "../../components/Common/BreadCrumb";
import Loader from "../../components/Loader";
import { WorkflowTemplate } from "./types";
import { ConfirmModal } from "./ui/Dialogs";

/**
 * Automation → Templates. System templates come with the platform (their texts
 * are translated by code); the cabinet's own are saved from scenarios.
 */
const TemplateGallery = () => {
    const { t } = useTranslation(['workflow', 'menu', 'common']);
    const navigate = useNavigate();
    const [items, setItems] = useState<WorkflowTemplate[] | null>(null);
    const [category, setCategory] = useState('');
    const [busy, setBusy] = useState<string | null>(null);
    const [removing, setRemoving] = useState<WorkflowTemplate | null>(null);

    const load = () => getWorkflowTemplates({ maxResults: 200 }).then((r: any) => setItems(r.items || [])).catch(() => setItems([]));
    useEffect(() => {
        load();
    }, []);

    const categories = useMemo(() => Array.from(new Set((items || []).map((i) => i.category).filter(Boolean))) as string[], [items]);
    const visible = (items || []).filter((i) => !category || i.category === category);

    const name = (tpl: WorkflowTemplate) => (tpl.isSystem && tpl.code ? t('workflow:template_' + tpl.code + '_name', { defaultValue: tpl.name }) : tpl.name);
    const description = (tpl: WorkflowTemplate) => (tpl.isSystem && tpl.code ? t('workflow:template_' + tpl.code + '_description', { defaultValue: tpl.description || '' }) : tpl.description);

    const use = (tpl: WorkflowTemplate) => {
        setBusy(tpl.uuid);
        applyWorkflowTemplate(tpl.uuid, { name: name(tpl) })
            .then((r: any) => navigate(route.AUTOMATION_SCENARIO.replace(':uuid', r.uuid)))
            .catch((e: any) => toast.error(e?.message || t('workflow:actionFailed')))
            .finally(() => setBusy(null));
    };

    const remove = () => removing &&
        removeWorkflowTemplate(removing.uuid)
            .then(() => { setRemoving(null); load(); })
            .catch((e: any) => toast.error(e?.message || t('workflow:actionFailed')));

    const chip = (on: boolean) => 'btn btn-sm ' + (on ? 'btn-primary' : 'btn-light');

    return (
        <>
            <BreadCrumb title={t('menu:automationTemplates')} pageTitle={t('menu:automation')} />
            {categories.length > 1 &&
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <button type="button" className={chip(category === '')} onClick={() => setCategory('')}>{t('workflow:allCategories')}</button>
                    {categories.map((c) => (
                        <button key={c} type="button" className={chip(category === c)} onClick={() => setCategory(c)}>
                            {t('workflow:category_' + c, { defaultValue: c })}
                        </button>
                    ))}
                </div>
            }
            {items === null ? <Loader /> :
                visible.length === 0 ? <div className="text-center text-muted py-5"><i className="ri-layout-grid-line display-5 d-block mb-2" /><p className="fs-14 mb-0">{t('workflow:noTemplates')}</p></div> :
                    <Row>
                        {visible.map((tpl) => (
                            <Col key={tpl.uuid} xxl={3} lg={4} md={6}>
                                <Card className="card-height-100">
                                    <CardBody className="d-flex flex-column">
                                        <div className="d-flex align-items-start gap-3 mb-3">
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={"avatar-title rounded fs-20 " + (tpl.isSystem ? 'bg-soft-primary text-primary' : 'bg-soft-success text-success')}>
                                                    <i className={tpl.isSystem ? 'ri-magic-line' : 'ri-bookmark-line'} />
                                                </span>
                                            </div>
                                            <h5 className="fs-15 mb-0 flex-grow-1 lh-base">{name(tpl)}</h5>
                                            {!tpl.isSystem && tpl.actions?.canRemove &&
                                                <button type="button" className="btn btn-sm btn-ghost-danger btn-icon" title={t('common:delete')} onClick={() => setRemoving(tpl)}><i className="ri-delete-bin-line" /></button>
                                            }
                                        </div>
                                        <p className="text-muted fs-14 flex-grow-1">{description(tpl)}</p>
                                        <div className="d-flex flex-wrap gap-1 mb-3">
                                            {(tpl.tags || []).map((tag) => <span key={tag} className="badge badge-soft-secondary">{tag}</span>)}
                                        </div>
                                        <button type="button" className="btn btn-soft-primary w-100" disabled={busy !== null} onClick={() => use(tpl)}>
                                            {busy === tpl.uuid ? <span className="spinner-border spinner-border-sm" /> : <><i className="ri-add-line align-bottom me-1" />{t('workflow:useTemplate')}</>}
                                        </button>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
            }
            <ConfirmModal
                show={removing !== null}
                title={t('common:delete')}
                message={t('workflow:confirmDeleteTemplate', { name: removing?.name })}
                confirmLabel={t('common:delete')}
                danger
                onConfirm={remove}
                onClose={() => setRemoving(null)}
            />
        </>
    );
};

export default TemplateGallery;
